import { Col, DatePicker, Input, InputNumber, Row } from 'antd'
import { HelpMessage } from 'utils/constants'
import { CASStatus, Permission } from 'shared'
import { useSubmittalTrackerContext } from '../../../../../../SubmittalTrackerContext'
import { Submittal } from '../../../../../../SubmittalTrackerContext/types'
import { useCheckPermission } from 'hooks/useCheckPermission'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSSelect from 'components/CIMSSelect'
import PayItemSelect from 'components/CIMSSelect/common/PayItemSelect'
import ExternalLinkEditor from 'components/ExternalLinkEditor'
import { SharePointRegexValidation } from 'shared'
import { useState } from 'react'
import { useDisabledOptions, DisabledOptions } from 'hooks/useDisabledOptions'
import CustomFieldEditor from 'components/CustomFields/CustomFieldEditor'

export const EditModalBody: React.VFC<Submittal> = ({
  statusId,
  packageNumber,
}) => {
  const {
    quickLinks,
    metaFetching,
    allPackageNumbers,
    selectOptions: { typeOptions, subtypeOptions, routingGroupOptions },
  } = useSubmittalTrackerContext()
  const help = HelpMessage.CAS.SubmittalTracker
  const canEditNumberName = useCheckPermission(
    Permission.EditNumberNameConstructionAdminSubmittal
  )
  const { invalidChars, whitespace } = SharePointRegexValidation
  const [filteredPackageNumbers] = useState<string[]>(
    allPackageNumbers.filter((number) => number !== packageNumber)
  )
  const lockedOptions: DisabledOptions = {
    disabled: statusId === CASStatus.Closed.id,
    reason: { why: 'invalid status', statusName: CASStatus.Closed.name },
  }
  const [lockedDisabledOptions, { some: lockedDisabled }] =
    useDisabledOptions(lockedOptions)
  const [lockedREDisabledOptions, { some: lockedREDisabled }] =
    useDisabledOptions([
      lockedOptions,
      {
        disabled: statusId === CASStatus.REReview.id,
        reason: { why: 'invalid status', statusName: CASStatus.REReview.name },
      },
    ])
  const [numberNameDisabledOptions, { some: numberNameDisabled }] =
    useDisabledOptions([
      lockedOptions,
      { disabled: !canEditNumberName, reason: 'missing permission' },
    ])
  const gutter = 16

  return (
    <>
      <Row gutter={gutter}>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="packageNumber"
            label="Package Number"
            requiredMessage="Please enter a package number"
            invalid={[
              {
                testPattern: invalidChars.regex,
                message: invalidChars.message,
              },
              {
                testPattern: whitespace.regex,
                message: whitespace.message,
              },
            ]}
            customValidator={{
              validator: (number) => !filteredPackageNumbers.includes(number),
              message: 'Package Number already in use',
            }}
            validateFirst="parallel"
            disabled={numberNameDisabledOptions}
            help={help.PackageNumber}
          >
            <Input disabled={numberNameDisabled} allowClear />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="packageName"
            label="Package Name"
            requiredMessage="Please enter a package name"
            invalid={[
              {
                testPattern: invalidChars.regex,
                message: invalidChars.message,
              },
              {
                testPattern: whitespace.regex,
                message: whitespace.message,
              },
            ]}
            validateFirst="parallel"
            disabled={numberNameDisabledOptions}
            help={help.PackageName}
          >
            <Input disabled={numberNameDisabled} allowClear />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="type"
            label="Type"
            requiredMessage="Please select a type"
            disabled={lockedDisabledOptions}
            help={help.Type}
          >
            <CIMSSelect options={typeOptions} disabled={lockedDisabled} />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="subtype"
            label="Subtype"
            disabled={lockedDisabledOptions}
            help={help.Subtype}
          >
            <CIMSSelect options={subtypeOptions} disabled={lockedDisabled} />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="quantity"
            label="Quantity"
            requiredMessage="Please enter a quantity"
            help={help.Quantity}
          >
            <InputNumber style={{ width: '100%' }} />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="units"
            label="Units"
            requiredMessage="Please enter units"
            help={help.Units}
          >
            <Input />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="requestedReviewDate"
            label="Requested Review Date"
            requiredMessage="Please select a requested review date"
            disabled={lockedDisabledOptions}
            help={help.RequestedReviewDate}
          >
            <DatePicker allowClear disabled={lockedDisabled} />
          </CIMSFormItem>
        </Col>
        <Col xs={24} xl={12}>
          <CIMSFormItem
            name="contractualReviewDate"
            label="Contractual Review Date"
            disabled={lockedDisabledOptions}
            help={help.ContractualReviewDate}
          >
            <DatePicker allowClear disabled={lockedDisabled} />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col xs={24}>
          <CIMSFormItem
            label="Routing Groups"
            name="routingGroups"
            help={help.RoutingGroups}
            requiredMessage={
              statusId === CASStatus.GroupReview.id
                ? 'Please select at least one routing group'
                : undefined
            }
            disabled={lockedREDisabledOptions}
          >
            <CIMSSelect
              disabled={lockedREDisabled}
              multiple
              options={routingGroupOptions}
            />
          </CIMSFormItem>
        </Col>
      </Row>
      <Row gutter={gutter}>
        <Col xs={24}>
          <CIMSFormItem name="payItems" label="Pay Items" help={help.PayItems}>
            <PayItemSelect multiple />
          </CIMSFormItem>
        </Col>
      </Row>
      <CustomFieldEditor
        alreadyDisabled={
          lockedDisabled
            ? { why: 'invalid status', statusName: CASStatus.Closed.name }
            : false
        }
        hideBooleans
      />
      <CIMSFormItem name="externalLinks" disabled={lockedDisabledOptions}>
        <ExternalLinkEditor
          quickLinkOptions={{ loading: metaFetching, quickLinks }}
          disabled={lockedDisabled}
          messages={{ editPackageNumberName: true }}
        />
      </CIMSFormItem>
    </>
  )
}
